<script>
import Layout from '@layouts/main'
import PageHeader from '@components/page-header'
import requestFunctions from '@src/functions/requestFunctions'

/**
 * Диалоговый кэш бота (админ) — «смысловая карта».
 * Данные: GET /v1/dialog-cache/semantic (только чтение) — связки llm_cache с эмбеддингами.
 * Кластеризация выполняется в браузере: похожие фразы бота складываются в один блок
 * (варианты внутри), похожие реплики клиента — в одно «намерение».
 * Все решения человека (склейки, каноны, позиции) пока хранятся локально в браузере —
 * запись в кэш появится на следующем этапе.
 */

const TH_BOT = 0.95 // порог АВТОсклейки фраз бота: только почти дословные (инцидент 26.07:
// на 0.92 автомат слил разные ступени диалога — «уточните, какие работы» и «извините...какие
// работы» — текстово похожи, по роли различны; всё спорное теперь решает человек)
const TH_INTENT = 0.88 // порог склейки реплик клиента
const TH_SUGGEST = 0.86 // нижняя граница «спорной склейки» — предлагаем человеку

function b64ToVec(b64) {
  if (!b64) return null
  try {
    const raw = atob(b64)
    const n = Math.floor(raw.length / 4)
    const buf = new ArrayBuffer(raw.length)
    const view = new Uint8Array(buf)
    for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i)
    const v = new Float32Array(buf, 0, n)
    let s = 0
    for (let i = 0; i < n; i++) s += v[i] * v[i]
    const norm = Math.sqrt(s) || 1
    const out = new Float32Array(n)
    for (let i = 0; i < n; i++) out[i] = v[i] / norm
    return out
  } catch (e) {
    return null
  }
}
function cos(a, b) {
  if (!a || !b) return -1
  const n = Math.min(a.length, b.length)
  let s = 0
  for (let i = 0; i < n; i++) s += a[i] * b[i]
  return s
}
function normText(t) {
  return (t || '')
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[^a-zа-я0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}
// Переменные в фразе: числа, даты, телефоны, суммы — такие фразы опасно клеить по смыслу
function hasVariables(t) {
  const s = (t || '').toLowerCase()
  return (
    /\d/.test(s) ||
    /(января|февраля|марта|апреля|мая|июня|июля|августа|сентября|октября|ноября|декабря)/.test(s) ||
    /(понедельник|вторник|среда|четверг|пятниц|суббот|воскресень)/.test(s) ||
    /(рубл|тысяч|процент)/.test(s)
  )
}

export default {
  page: { title: 'Диалоговый кэш' },
  components: { Layout, PageHeader },
  data() {
    return {
      title: 'Диалоговый кэш — смысловая карта',
      loading: true,
      error: null,
      links: [], // сырые связки из API (с векторами)
      voiced: {}, // norm -> {text, voice_id, hit_count}
      stats: null,

      mode: 'semantic', // 'semantic' | 'verbatim'
      fullscreen: true, // холст на весь экран (реш. Сергея 24.07: работать в маленькой зоне невозможно)
      showSuggest: true, // рисовать предложения склейки прямо на холсте
      suggestSel: null, // выбранное предложение склейки (карточка сравнения)
      nodes: [], // блоки на холсте
      edges: [], // {srcId, dstId, label, count, hits, texts}
      suggestions: [], // спорные склейки [{aId,bId,sim,aText,bText,vars}]
      decisions: {}, // ключ пары -> 'merge'|'skip' (localStorage)
      deleted: {}, // keyNorm -> текст канона на момент удаления (локальная «корзина», localStorage)
      removedLinks: {}, // «реплика не должна вести в этот блок»: ключ src>реплика>dst -> подпись
      splits: {}, // norm -> текст: формулировка вытащена в ОТДЕЛЬНЫЙ блок (не склеивать по смыслу)
      trashOpen: false,
      nodeCustom: '', // (не используется, оставлено для совместимости localStorage)
      editText: '', // редактируемый текст фразы в карточке
      editingId: null, // блок, редактируемый прямо на холсте (dblclick)
      editingText: '',
      showVariants: false, // формулировки из кэша — под спойлером (артефакт дословного кэша)
      varQ: '',
      varPage: 0,
      outQ: '',
      outPage: 0,
      inQ: '',
      inPage: 0,
      saving: false,
      saveMsg: '',
      callsOpen: false, // левая панель «Диалоги»
      callsList: [],
      callsLoading: false,
      activeCall: null, // {call, turns:[{time,client,response,from_cache,nodeId,step,offset}]}
      callGhosts: [], // «призрачные» блоки открытого звонка (фразы, которых нет в кэше)
      recPos: -1, // текущая секунда проигрывания записи звонка
      recSpeed: 1,
      mergeCustom: '', // поле «свой вариант» в карточке склейки
      mergeChoice: 'a', // какой вариант оставить каноном при склейке: 'a' | 'b'
      review: false, // режим «разобрать склейки по одной»: после решения открывается следующая
      reviewSkipped: {}, // пары, пропущенные в текущем прогоне
      canons: {}, // nodeId(текст канона нормализованный ключ кластера) -> выбранный текст канона
      positions: {}, // nodeId -> [x,y] (localStorage)

      selectedId: null,
      hoverId: null,
      search: '',
      searchHits: [],
      searchIdx: -1,

      scale: 0.8,
      tx: 40,
      ty: 20,
      panning: false,
      dragNode: null,
      moved: false,

      playingKey: null,
      audioEl: null,
    }
  },
  computed: {
    bot_uuid() {
      return this.$route.params.uuid
    },
    nodeById() {
      const m = {}
      this.nodes.forEach((n) => {
        m[n.id] = n
      })
      return m
    },
    selectedNode() {
      return this.selectedId ? this.nodeById[this.selectedId] : null
    },
    // стрелки рисуем только у выбранного/наведённого блока — иначе холст чистый (реш. Сергея 23.07)
    visibleEdges() {
      const focus = this.selectedId || this.hoverId
      if (!focus) return []
      return this.edges.filter((e) => e.srcId === focus || e.dstId === focus)
    },
    neighborIds() {
      const focus = this.selectedId
      if (!focus) return null
      const s = { [focus]: true }
      this.edges.forEach((e) => {
        if (e.srcId === focus) s[e.dstId] = true
        if (e.dstId === focus) s[e.srcId] = true
      })
      return s
    },
    searchHitIds() {
      const q = this.search.trim().toLowerCase()
      if (!q) return null
      const s = {}
      this.nodes.forEach((n) => {
        const hit =
          n.canon.toLowerCase().includes(q) ||
          n.variants.some((v) => v.text.toLowerCase().includes(q)) ||
          n.entries.some((en) => en.label.toLowerCase().includes(q))
        if (hit) s[n.id] = true
      })
      return s
    },
    callIncoming() {
      if (!this.activeCall || !this.selectedId) return []
      return this.activeCall.turns
        .filter((t) => t.nodeId === this.selectedId && t.client)
        .map((t) => ({
          client: t.client,
          prev: t.prev,
          time: t.time,
          inCache: this.selIncoming.some((e) => normText(e.label) === normText(t.client)),
        }))
    },
    filtVariants() {
      if (!this.selectedNode) return []
      const q = this.varQ.trim().toLowerCase()
      return q ? this.selectedNode.variants.filter((v) => v.text.toLowerCase().includes(q)) : this.selectedNode.variants
    },
    pageVariants() {
      return this.filtVariants.slice(this.varPage * 6, this.varPage * 6 + 6)
    },
    filtOut() {
      const q = this.outQ.trim().toLowerCase()
      return this.selOutgoing.filter((e) => {
        if (!q) return true
        const dst = this.nodeById[e.dstId]
        return e.label.toLowerCase().includes(q) || (dst && dst.canon.toLowerCase().includes(q))
      })
    },
    pageOut() {
      return this.filtOut.slice(this.outPage * 6, this.outPage * 6 + 6)
    },
    filtIn() {
      const q = this.inQ.trim().toLowerCase()
      return this.selIncoming.filter((e) => {
        if (!q) return true
        const src = this.nodeById[e.srcId]
        return e.label.toLowerCase().includes(q) || (src && src.canon.toLowerCase().includes(q))
      })
    },
    pageIn() {
      return this.filtIn.slice(this.inPage * 6, this.inPage * 6 + 6)
    },
    selIncoming() {
      if (!this.selectedId) return []
      return this.edges.filter((e) => e.dstId === this.selectedId)
    },
    selOutgoing() {
      if (!this.selectedId) return []
      return this.edges.filter((e) => e.srcId === this.selectedId)
    },
    openSuggestions() {
      return this.suggestions.filter((s) => !this.decisions[s.key])
    },
    // пунктирные связи «похожие узлы» прямо на холсте
    suggestLinks() {
      if (!this.showSuggest || this.mode !== 'semantic') return []
      return this.openSuggestions
        .map((s) => {
          const a = this.nodeById[s.aId]
          const b = this.nodeById[s.bId]
          if (!a || !b) return null
          const x1 = a.x + 170
          const y1 = a.y + (a.h || 120) / 2
          const x2 = b.x + 170
          const y2 = b.y + (b.h || 120) / 2
          return {
            s,
            d: `M${x1},${y1} C${x1},${(y1 + y2) / 2} ${x2},${(y1 + y2) / 2} ${x2},${y2}`,
            mx: (x1 + x2) / 2,
            my: (y1 + y2) / 2,
          }
        })
        .filter(Boolean)
    },
    pathSet() {
      if (!this.activeCall) return null
      const m = {}
      this.activeCall.turns.forEach((t) => {
        if (t.nodeId) (m[t.nodeId] = m[t.nodeId] || []).push(t.step)
      })
      const out = {}
      Object.keys(m).forEach((k) => { out[k] = m[k].join(',') })
      return out
    },
    playingTurnIdx() {
      if (!this.activeCall || this.recPos < 0) return -1
      let idx = -1
      this.activeCall.turns.forEach((t, i) => {
        if (t.offset !== null && t.offset <= this.recPos) idx = i
      })
      return idx
    },
    playingNodeId() {
      const i = this.playingTurnIdx
      if (i < 0) return null
      const t = this.activeCall.turns[i]
      return t ? (t.nodeId || t.ghostId || null) : null
    },
    pathSegs() {
      if (!this.activeCall) return []
      const segs = []
      let prev = null
      const gmap = {}
      this.callGhosts.forEach((x) => { gmap[x.id] = x })
      this.activeCall.turns.forEach((t) => {
        const id = t.nodeId || t.ghostId
        if (!id) return
        if (prev && prev !== id) {
          const a = this.nodeById[prev] || gmap[prev]
          const b = this.nodeById[id] || gmap[id]
          if (a && b) {
            const x1 = a.x + 340
            const y1 = a.y + Math.min(60, (a.h || 120) / 2)
            const x2 = b.x - 8
            const y2 = b.y + Math.min(60, (b.h || 120) / 2)
            const dx = Math.max(60, Math.abs(x2 - x1) / 2)
            segs.push({ d: `M${x1},${y1} C${x1 + dx},${y1} ${x2 - dx},${y2} ${x2},${y2}` })
          }
        }
        prev = id
      })
      return segs
    },
    worldSize() {
      let w = 1200
      let h = 800
      this.nodes.forEach((n) => {
        w = Math.max(w, n.x + 360)
        h = Math.max(h, n.y + (n.h || 150) + 80)
      })
      this.callGhosts.forEach((n) => {
        w = Math.max(w, n.x + 360)
        h = Math.max(h, n.y + (n.h || 150) + 80)
      })
      return { w, h }
    },
  },
  watch: {
    playingNodeId(id) {
      if (id) this.centerOn(this.nodeById[id])
    },
    selectedId() {
      const n = this.selectedId ? this.nodeById[this.selectedId] : null
      this.editText = n ? n.canon : ''
      this.showVariants = false
      this.saveMsg = ''
      this.varQ = ''
      this.varPage = 0
      this.outQ = ''
      this.outPage = 0
      this.inQ = ''
      this.inPage = 0
    },
    suggestSel() {
      this.mergeCustom = ''
      this.mergeChoice = 'a'
    },
    varQ() {
      this.varPage = 0
    },
    outQ() {
      this.outPage = 0
    },
    inQ() {
      this.inPage = 0
    },
  },
  created() {
    this.lsKey = 'dc2_' + (this.$route.params.uuid || '')
    try {
      const saved = JSON.parse(window.localStorage.getItem(this.lsKey) || '{}')
      this.decisions = saved.decisions || {}
      this.canons = saved.canons || {}
      this.positions = saved.positions || {}
      this.deleted = saved.deleted || {}
      this.removedLinks = saved.removedLinks || {}
      this.splits = saved.splits || {}
    } catch (e) {
      /* повреждённый localStorage не должен ронять страницу */
    }
    this.fetchData()
  },
  mounted() {
    this._esc = (ev) => {
      if (ev.key === 'Escape') {
        this.selectedId = null
        this.suggestSel = null
        this.trashOpen = false
        this.review = false
      }
    }
    window.addEventListener('keydown', this._esc)
  },
  beforeDestroy() {
    if (this.audioEl) this.audioEl.pause()
    if (this._esc) window.removeEventListener('keydown', this._esc)
  },
  methods: {
    persist() {
      window.localStorage.setItem(
        this.lsKey,
        JSON.stringify({
          decisions: this.decisions,
          canons: this.canons,
          positions: this.positions,
          deleted: this.deleted,
          removedLinks: this.removedLinks,
          splits: this.splits,
        })
      )
    },
    async fetchData(quiet) {
      if (!quiet) this.loading = true
      this.error = null
      try {
        const resp = await requestFunctions.sendDataAsync(
          'https://api.client.za-bota.com/v1/dialog-cache/semantic',
          { bot_uuid: this.bot_uuid },
          'GET'
        )
        if (!resp || resp.status !== 'success') {
          this.error = (resp && resp.message) || 'Не удалось загрузить данные'
          return
        }
        this.voiced = resp.data.voiced || {}
        this.stats = resp.data.stats || null
        const raw = resp.data.links || []
        // векторы держим ВНЕ реактивных данных Vue: Float32Array в data() = сотни тысяч геттеров
        this._vPrev = raw.map((l) => b64ToVec(l.emb_prev))
        this._vUser = raw.map((l) => b64ToVec(l.emb_user))
        this.links = raw.map((l, i) => ({
          i,
          client: l.client,
          prev: l.prev,
          response: l.response,
          hit_count: l.hit_count,
        }))
        this.rebuild()
      } catch (e) {
        this.error = 'Ошибка загрузки: ' + e.message
      } finally {
        this.loading = false
      }
    },

    // ---------- кластеризация ----------
    rebuild() {
      // 1) дословные фразы бота: norm -> {texts:{}, uses, vec}
      const verb = {}
      const touch = (text, vec, uses) => {
        const n = normText(text)
        if (!n) return null
        if (!verb[n]) verb[n] = { norm: n, texts: {}, uses: 0, vec: null }
        verb[n].texts[text] = (verb[n].texts[text] || 0) + uses
        verb[n].uses += uses
        if (!verb[n].vec && vec) verb[n].vec = vec
        return n
      }
      this.links.forEach((l) => {
        l._prev = touch(l.prev, this._vPrev[l.i], 1 + l.hit_count)
        l._resp = touch(l.response, null, 1 + l.hit_count)
      })
      // вектор ответа: если этот же текст где-то встречался как prev — вектор уже есть
      const verbs = Object.values(verb).sort((a, b) => b.uses - a.uses)

      // 2) смысловые кластеры (или дословные — по режиму)
      const semantic = this.mode === 'semantic'
      const clusters = []
      const byNorm = {}
      verbs.forEach((v) => {
        let target = null
        if (semantic && v.vec && !this.splits[v.norm]) {
          let best = 0
          clusters.forEach((c) => {
            if (!c.vec || c.split) return
            const s = cos(v.vec, c.vec)
            if (s >= TH_BOT && s > best) {
              best = s
              target = c
            }
          })
        }
        if (!target) {
          target = { id: 'c' + clusters.length, members: [], vec: v.vec, varsFlag: false, split: !!this.splits[v.norm] }
          clusters.push(target)
        }
        target.members.push(v)
        if (!target.vec && v.vec) target.vec = v.vec
        byNorm[v.norm] = target
      })

      // 2б) подтверждённые человеком склейки (только смысловой режим)
      if (semantic) {
        Object.keys(this.decisions).forEach((key) => {
          if (this.decisions[key] !== 'merge') return
          const [na, nb] = key.split('||')
          const ca = byNorm[na]
          const cb = byNorm[nb]
          if (!ca || !cb || ca === cb) return
          cb.members.forEach((m) => {
            ca.members.push(m)
            byNorm[m.norm] = ca
          })
          cb.members = []
        })
      }
      const live = clusters.filter((c) => c.members.length && !this.deleted[c.members[0].norm])

      // 3) узлы
      const nodes = live.map((c) => {
        const variants = []
        c.members.forEach((m) => {
          Object.keys(m.texts).forEach((t) => {
            variants.push({
              text: t,
              uses: m.texts[t],
              voiced: !!this.voiced[normText(t)],
              norm: m.norm,
            })
          })
        })
        variants.sort((a, b) => b.uses - a.uses)
        const keyNorm = c.members[0].norm
        const canonSaved = this.canons[keyNorm]
        const canon = canonSaved || variants[0].text
        const vars = variants.some((v) => hasVariables(v.text))
        return {
          id: c.id,
          keyNorm,
          canon,
          variants,
          uses: variants.reduce((s, v) => s + v.uses, 0),
          voiced: !!this.voiced[normText(canon)],
          voicedAny: variants.some((v) => v.voiced),
          voicedCount: variants.filter((v) => v.voiced).length,
          vars,
          customCanon: !!(canonSaved && !variants.some((v) => v.text === canonSaved)),
          entries: [], // входы (намерения клиента) — заполняются ниже, показываются СВЕРХУ блока
          x: 0,
          y: 0,
          level: 0,
        }
      })
      // векторы узлов — вне реактивных объектов (см. fetchData)
      const nodeVec = {}
      live.forEach((c, i) => {
        nodeVec[nodes[i].id] = c.vec
      })
      const nodeByNorm = {}
      nodes.forEach((n, i) => {
        live[i].members.forEach((m) => {
          nodeByNorm[m.norm] = n
        })
      })

      // 4) намерения клиента и рёбра: (узел-источник, кластер реплик, узел-цель)
      const edgeMap = {}
      const bySrc = {}
      this.links.forEach((l) => {
        const src = l._prev ? nodeByNorm[l._prev] : null
        const dst = l._resp ? nodeByNorm[l._resp] : null
        if (!dst) return
        const sid = src ? src.id : 'start'
        if (!bySrc[sid]) bySrc[sid] = []
        bySrc[sid].push({ l, src, dst })
      })
      Object.keys(bySrc).forEach((sid) => {
        const intents = []
        bySrc[sid].forEach(({ l, src, dst }) => {
          const vUser = this._vUser[l.i]
          let target = null
          if (semantic && vUser) {
            let best = 0
            intents.forEach((it) => {
              if (it.dst !== dst || !it.vec) return
              const s = cos(vUser, it.vec)
              if (s >= TH_INTENT && s > best) {
                best = s
                target = it
              }
            })
          } else {
            target = intents.find((it) => it.dst === dst && normText(it.label) === normText(l.client)) || null
          }
          if (!target) {
            target = { src, dst, vec: vUser, label: l.client, texts: [], count: 0, hits: 0 }
            intents.push(target)
          }
          target.texts.push(l.client)
          target.count++
          target.hits += l.hit_count
          if (l.client.length < target.label.length) target.label = l.client
        })
        intents.forEach((it) => {
          const stableKey = (it.src ? it.src.keyNorm : 'start') + '>' + normText(it.label) + '>' + it.dst.keyNorm
          if (this.removedLinks[stableKey]) return
          it.stableKey = stableKey
          const key = (it.src ? it.src.id : 'start') + '>' + it.dst.id + '>' + normText(it.label)
          if (!edgeMap[key]) {
            edgeMap[key] = {
              srcId: it.src ? it.src.id : null,
              dstId: it.dst.id,
              stableKey,
              label: it.label,
              count: it.count,
              hits: it.hits,
              texts: it.texts,
            }
          }
          it.dst.entries.push({
            label: it.label,
            count: it.count,
            fromId: it.src ? it.src.id : null,
          })
        })
      })
      const edges = Object.values(edgeMap).filter((e) => e.srcId)
      nodes.forEach((n) => n.entries.sort((a, b) => b.count - a.count))

      // 5) уровни и раскладка (слева направо, барицентр по предкам)
      const adj = {}
      const hasIn = {}
      edges.forEach((e) => {
        if (!adj[e.srcId]) adj[e.srcId] = []
        adj[e.srcId].push(e.dstId)
        hasIn[e.dstId] = true
      })
      const level = {}
      // настоящий вход = блок, куда клиент попадает с пустого «предыдущего хода» (начало звонка);
      // остальные корни без входов — обрывки цепочек (предыдущий ход не найден в кэше дословно)
      nodes.forEach((n) => {
        n.frag = false
      })
      let frontier = nodes.filter((n) => !hasIn[n.id]).map((n) => n.id)
      frontier.forEach((id) => {
        level[id] = 0
      })
      while (frontier.length) {
        const nxt = []
        frontier.forEach((id) => {
          ;(adj[id] || []).forEach((t) => {
            if (level[t] === undefined) {
              level[t] = level[id] + 1
              nxt.push(t)
            }
          })
        })
        frontier = nxt
      }
      let maxl = 0
      Object.values(level).forEach((v) => {
        maxl = Math.max(maxl, v)
      })
      nodes.forEach((n) => {
        n.level = level[n.id] !== undefined ? level[n.id] : maxl + 1
      })
      const cols = {}
      nodes.forEach((n) => {
        if (!cols[n.level]) cols[n.level] = []
        cols[n.level].push(n)
      })
      const XGAP = 480
      const YGAP = 30
      // текст в блоке теперь полный — высоту блока оцениваем по длине текста и числу входов
      const estH = (n) => {
        const textLines = Math.ceil((n.canon.length || 1) / 44)
        const entryRows = n.entries.length ? Math.ceil(Math.min(n.entries.length, 4) / 2) : 0
        return 52 + entryRows * 26 + textLines * 18 + 12
      }
      const preds = {}
      edges.forEach((e) => {
        if (!preds[e.dstId]) preds[e.dstId] = []
        preds[e.dstId].push(e.srcId)
      })
      Object.keys(cols)
        .map(Number)
        .sort((a, b) => a - b)
        .forEach((L, i) => {
          const arr = cols[L]
          if (i === 0) {
            arr.forEach((n) => {
              n.frag = !n.entries.some((en) => !en.fromId)
            })
            arr.sort((a, b) => (a.frag ? 1 : 0) - (b.frag ? 1 : 0) || b.uses - a.uses)
          }
          else
            arr.sort((a, b) => {
              const bc = (n) => {
                const ys = (preds[n.id] || [])
                  .map((id) => this.nodesTmpY && this.nodesTmpY[id])
                  .filter((y) => y !== undefined)
                return ys.length ? ys.reduce((s, y) => s + y, 0) / ys.length : 1e9
              }
              return bc(a) - bc(b) || b.uses - a.uses
            })
          this.nodesTmpY = this.nodesTmpY || {}
          let y = 40
          arr.forEach((n) => {
            n.h = estH(n)
            n.x = 40 + L * XGAP
            n.y = y
            y += n.h + YGAP
            this.nodesTmpY[n.id] = n.y
          })
        })
      this.nodesTmpY = null
      // сохранённые вручную позиции
      const posKey = this.mode
      nodes.forEach((n) => {
        const p = (this.positions[posKey] || {})[n.keyNorm]
        if (p) {
          n.x = p[0]
          n.y = p[1]
        }
      })

      // 6) предложения спорных склеек (только смысловой режим): пары канонов в [TH_SUGGEST, TH_BOT)
      const suggestions = []
      if (semantic) {
        const prevSets = {}
        edges.forEach((e) => {
          if (!prevSets[e.dstId]) prevSets[e.dstId] = {}
          prevSets[e.dstId][e.srcId] = true
        })
        const ctxDiffers = (x, y) => {
          const a = prevSets[x]
          const b = prevSets[y]
          if (!a || !b) return false
          return !Object.keys(a).some((k) => b[k])
        }
        for (let i = 0; i < nodes.length; i++) {
          for (let j = i + 1; j < nodes.length; j++) {
            const a = nodes[i]
            const b = nodes[j]
            if (!nodeVec[a.id] || !nodeVec[b.id]) continue
            const s = cos(nodeVec[a.id], nodeVec[b.id])
            if (s >= TH_SUGGEST && s < TH_BOT) {
              const key = a.keyNorm + '||' + b.keyNorm
              suggestions.push({
                key,
                aId: a.id,
                bId: b.id,
                aText: a.canon,
                bText: b.canon,
                sim: s,
                vars: a.vars || b.vars,
                ctxDiff: ctxDiffers(a.id, b.id),
              })
            }
          }
        }
        suggestions.sort((x, y) => y.sim - x.sim)
        if (suggestions.length > 40) suggestions.length = 40
      }

      const normToNode = {}
      nodes.forEach((nn) => nn.variants.forEach((v) => { normToNode[v.norm] = nn.id }))
      this._normToNode = normToNode
      this.nodes = nodes
      this.edges = edges
      this.suggestions = suggestions
      this.selectedId = null
      this.hoverId = null
      this.suggestSel = null
      if (this._callRaw && this.activeCall) this.buildCallView()
    },

    // ---------- действия человека ----------
    decideSuggestion(s, verdict) {
      if (verdict === 'merge') {
        const canon = this.mergeCustom.trim() || (this.mergeChoice === 'b' ? s.bText : s.aText)
        const a = this.nodeById[s.aId]
        if (a) this.$set(this.canons, a.keyNorm, canon)
      }
      this.$set(this.decisions, s.key, verdict)
      this.persist()
      this.suggestSel = null
      this.mergeCustom = ''
      if (verdict === 'merge') this.rebuild()
      if (this.review) this.$nextTick(() => this.nextSuggestion())
    },
    // force = true — «Переозвучить»: синтезировать заново поверх готовой записи
    // (без него сервер отвечает «уже озвучено» и ничего не переделывает)
    async saveBlock(node, text, force = false) {
      this.setCanon(node, text)
      this.saving = true
      this.saveMsg = ''
      try {
        const payload = {
          bot_uuid: this.bot_uuid,
          text,
          // все формулировки блока: их ответы в кэше будут заменены на новый текст
          replace_variants: JSON.stringify([node.canon].concat(node.variants.map((v) => v.text)).slice(0, 200)),
        }
        if (force) payload.force = 1
        const resp = await requestFunctions.sendDataAsync(
          'https://api.client.za-bota.com/v1/dialog-cache/save-block',
          payload,
          'POST'
        )
        if (resp && resp.status === 'success') {
          this.$set(this.voiced, normText(text), { text, voice_id: '', hit_count: 0 })
          node.voiced = true
          node.voicedAny = true
          const nrep = (resp.data && resp.data.replaced) || 0
          const secs = (resp.data && resp.data.seconds) || 0
          this.saveMsg =
            (resp.data && resp.data.revoiced
              ? 'озвучка записана заново' + (secs ? ' (' + secs + ' с)' : '') + ', прежняя сохранена. '
              : resp.data && resp.data.new
              ? 'озвучка синтезирована и сохранена. '
              : 'озвучка уже была в кэше. ') +
            (nrep ? 'Бот теперь говорит этот текст (заменено ответов: ' + nrep + ')' : 'В кэше ответов эта фраза не встречается — бот скажет её, когда модель её сгенерит')
        } else {
          this.saveMsg = 'текст сохранён, но озвучка не записалась: ' + ((resp && resp.message) || 'ошибка')
        }
      } catch (e) {
        this.saveMsg = 'текст сохранён, но озвучка не записалась: ' + e.message
      } finally {
        this.saving = false
      }
    },
    async revoiceBlock(node, text) {
      if (!confirm('Записать озвучку этой фразы заново?\n\nТекст не меняется — бот просто проговорит её ещё раз и запишет новый файл. Прежняя запись сохранится рядом. Нужно, если в записи слышен брак.')) return
      await this.saveBlock(node, text, true)
    },
    startEditNode(n) {
      this.editingId = n.id
      this.editingText = n.canon
      this.selectedId = null
    },
    saveEditNode(n) {
      if (!this.editingText.trim()) return
      this.saveBlock(n, this.editingText.trim())
      this.editingId = null
    },
    setCanon(node, text) {
      this.$set(this.canons, node.keyNorm, text)
      this.persist()
      node.canon = text
      node.voiced = !!this.voiced[normText(text)]
      node.customCanon = !node.variants.some((v) => v.text === text)
      if (this.selectedId === node.id) this.editText = text
    },
    async deleteNode(node) {
      const uses = node.uses || 0
      if (!confirm('Удалить блок из кэша бота?\n\nБот перестанет отвечать этой заготовкой (' + uses + ' использований) и на этих ходах будет генерировать ответ заново. Озвучки останутся в библиотеке.')) return
      try {
        const resp = await requestFunctions.sendDataAsync(
          'https://api.client.za-bota.com/v1/dialog-cache/delete-block',
          { bot_uuid: this.bot_uuid, texts: JSON.stringify([node.canon].concat(node.variants.map((v) => v.text)).slice(0, 200)) },
          'POST'
        )
        if (!resp || resp.status !== 'success') {
          alert('Не удалось удалить из кэша: ' + ((resp && resp.message) || 'ошибка'))
          return
        }
      } catch (e) {
        alert('Не удалось удалить из кэша: ' + e.message)
        return
      }
      this.$set(this.deleted, node.keyNorm, node.canon)
      this.persist()
      this.selectedId = null
      this.rebuild()
    },
    async clearBotCache() {
      const v = prompt('ВНИМАНИЕ: кэш ответов бота будет ПОЛНОСТЬЮ очищен.\n\nБот заново наберёт его из живых звонков (первые звонки — целиком на генерации). Озвучки остаются.\n\nЧтобы подтвердить, введите слово ОЧИСТИТЬ:')
      if (v === null) return
      if (v.trim().toUpperCase() !== 'ОЧИСТИТЬ') { alert('Очистка отменена — подтверждение не совпало.') ; return }
      try {
        const resp = await requestFunctions.sendDataAsync(
          'https://api.client.za-bota.com/v1/dialog-cache/clear-cache',
          { bot_uuid: this.bot_uuid, confirm: 'CLEAR' },
          'POST'
        )
        if (resp && resp.status === 'success') {
          alert('Кэш очищен: удалено записей — ' + resp.data.deleted + '. Резервная копия сохранена (' + resp.data.backup + ').')
          window.location.reload()
        } else {
          alert('Не удалось очистить кэш: ' + ((resp && resp.message) || 'ошибка'))
        }
      } catch (e) {
        alert('Не удалось очистить кэш: ' + e.message)
      }
    },
    restoreNode(key) {
      this.$delete(this.deleted, key)
      this.persist()
      this.rebuild()
    },
    setMode(m) {
      if (this.mode === m) return
      this.mode = m
      this.rebuild()
      this.fit()
    },

    // ---------- холст ----------
    fit() {
      this.scale = 0.8
      this.tx = 40
      this.ty = 20
      const start = this.nodes.filter((n) => n.level === 0).sort((a, b) => b.uses - a.uses)[0]
      if (start) {
        const vp = this.$refs.viewport
        const h = vp ? vp.clientHeight : 600
        this.tx = 60 - start.x * this.scale
        this.ty = h / 2 - (start.y + 70) * this.scale
      }
    },
    onWheel(ev) {
      ev.preventDefault()
      const k = ev.deltaY < 0 ? 1.12 : 1 / 1.12
      const ns = Math.min(2.2, Math.max(0.12, this.scale * k))
      const r = this.$refs.viewport.getBoundingClientRect()
      const cx = ev.clientX - r.left
      const cy = ev.clientY - r.top
      this.tx = cx - ((cx - this.tx) * ns) / this.scale
      this.ty = cy - ((cy - this.ty) * ns) / this.scale
      this.scale = ns
    },
    onCanvasDown(ev) {
      if (ev.target.closest('.sm-node, .sm-bar, .sm-schip')) return
      this.panning = { x: ev.clientX - this.tx, y: ev.clientY - this.ty, sx: ev.clientX, sy: ev.clientY }
    },
    splitVariant(node, v) {
      this.$set(this.splits, v.norm, v.text)
      this.persist()
      this.selectedId = null
      this.rebuild()
    },
    unsplit(key) {
      this.$delete(this.splits, key)
      this.persist()
      this.rebuild()
    },
    removeLink(e) {
      const src = this.nodeById[e.srcId]
      const dst = this.nodeById[e.dstId]
      const label = '«' + this.short(e.label, 50) + '» → ' + this.short(dst ? dst.canon : '', 60)
      this.$set(this.removedLinks, e.stableKey || ((src ? src.keyNorm : 'start') + '>' + normText(e.label) + '>' + (dst ? dst.keyNorm : '')), label)
      this.persist()
      this.rebuild()
    },
    restoreLink(key) {
      this.$delete(this.removedLinks, key)
      this.persist()
      this.rebuild()
    },
    reviewQueue() {
      return this.openSuggestions.filter((x) => !this.reviewSkipped[x.key])
    },
    startReview() {
      this.review = true
      this.reviewSkipped = {}
      this.selectedId = null
      this.trashOpen = false
      this.nextSuggestion()
    },
    nextSuggestion() {
      const list = this.reviewQueue()
      if (!list.length) {
        this.review = false
        this.suggestSel = null
        return
      }
      this.suggestSel = list[0]
      this.centerPair(list[0])
    },
    skipSuggestion() {
      if (this.suggestSel) this.$set(this.reviewSkipped, this.suggestSel.key, true)
      this.nextSuggestion()
    },
    centerPair(sg) {
      const a = this.nodeById[sg.aId]
      const b = this.nodeById[sg.bId]
      if (!a || !b) return
      const vp = this.$refs.viewport
      const r = vp.getBoundingClientRect()
      const mx = (a.x + b.x) / 2 + 170
      const my = (a.y + (a.h || 120) / 2 + b.y + (b.h || 120) / 2) / 2
      // панель справа ~430px — центрируем в свободной зоне
      this.tx = (r.width - 430) / 2 - mx * this.scale
      this.ty = r.height / 2 - my * this.scale
    },
    entriesFor(id) {
      const n = this.nodeById[id]
      return n ? n.entries.slice(0, 3) : []
    },
    // самый частый вход в узел: {prev: фраза бота до этого, label: ответ клиента, count, more}
    topContext(id) {
      const inc = this.edges.filter((e) => e.dstId === id).sort((a, b) => b.count - a.count)
      if (!inc.length) return null
      const e = inc[0]
      const prev = this.nodeById[e.srcId]
      return { prev: prev ? prev.canon : '', label: e.label, count: e.count, more: inc.length - 1 }
    },
    openSuggest(link) {
      this.suggestSel = link.s
      this.selectedId = null
    },
    onNodeDown(node, ev) {
      if (ev.target.closest('.sm-play')) return
      this.dragNode = { node, mx: ev.clientX, my: ev.clientY, nx: node.x, ny: node.y }
      this.moved = false
    },
    onMove(ev) {
      if (this.panning) {
        this.tx = ev.clientX - this.panning.x
        this.ty = ev.clientY - this.panning.y
      }
      if (this.dragNode) {
        const d = this.dragNode
        d.node.x = d.nx + (ev.clientX - d.mx) / this.scale
        d.node.y = d.ny + (ev.clientY - d.my) / this.scale
        this.moved = true
      }
    },
    onUp(ev) {
      if (this.panning && ev && Math.abs(ev.clientX - this.panning.sx) < 4 && Math.abs(ev.clientY - this.panning.sy) < 4) {
        this.selectedId = null
        this.suggestSel = null
        this.trashOpen = false
        this.review = false
      }
      if (this.dragNode && this.moved) {
        const posKey = this.mode
        if (!this.positions[posKey]) this.$set(this.positions, posKey, {})
        this.$set(this.positions[posKey], this.dragNode.node.keyNorm, [this.dragNode.node.x, this.dragNode.node.y])
        this.persist()
      }
      this.panning = false
      this.dragNode = null
    },
    clickNode(node) {
      if (this.moved || this.editingId === node.id) return
      this.selectedId = this.selectedId === node.id ? null : node.id
    },
    centerOn(node) {
      const vp = this.$refs.viewport
      const r = vp.getBoundingClientRect()
      this.tx = r.width / 2 - (node.x + 170) * this.scale
      this.ty = r.height / 2 - (node.y + 75) * this.scale
    },
    goTo(id) {
      const n = this.nodeById[id]
      if (!n) return
      this.centerOn(n)
      this.selectedId = id
    },
    edgePath(e) {
      const a = this.nodeById[e.srcId]
      const b = this.nodeById[e.dstId]
      if (!a || !b) return ''
      const x1 = a.x + 340
      const y1 = a.y + Math.min(60, (a.h || 120) / 2)
      const x2 = b.x - 8
      const y2 = b.y + Math.min(60, (b.h || 120) / 2)
      const dx = Math.max(60, Math.abs(x2 - x1) / 2)
      return `M${x1},${y1} C${x1 + dx},${y1} ${x2 - dx},${y2} ${x2},${y2}`
    },
    edgeMid(e) {
      const a = this.nodeById[e.srcId]
      const b = this.nodeById[e.dstId]
      if (!a || !b) return { x: 0, y: 0 }
      return { x: (a.x + 340 + b.x) / 2, y: (a.y + b.y + Math.min(60, (a.h || 120) / 2) + Math.min(60, (b.h || 120) / 2)) / 2 }
    },
    onSearchEnter() {
      const ids = Object.keys(this.searchHitIds || {})
      if (!ids.length) return
      this.searchIdx = (this.searchIdx + 1) % ids.length
      this.goTo(ids[this.searchIdx])
    },

    // ---------- аудио ----------
    audioUrl(text) {
      const token = window.localStorage.getItem('API_KEY_ZABOTA')
      return (
        'https://api.client.za-bota.com/v1/dialog-cache/audio?bot_uuid=' +
        encodeURIComponent(this.bot_uuid) +
        '&text=' + encodeURIComponent(text) +
        '&token=' + encodeURIComponent(token)
      )
    },
    async openCalls() {
      this.callsOpen = !this.callsOpen
      if (!this.callsOpen) {
        this.activeCall = null
        return
      }
      // без кнопки «Обновить»: при каждом открытии Диалогов подтягиваем свежие данные —
      // и список звонков, и связки/озвучки карты (звонок мог только что дописать кэш)
      this._refreshPromise = this.fetchData(true)
      this.callsLoading = true
      try {
        const resp = await requestFunctions.sendDataAsync(
          'https://api.client.za-bota.com/v1/dialog-cache/calls',
          { bot_uuid: this.bot_uuid },
          'GET'
        )
        if (resp && resp.status === 'success') this.callsList = resp.data.calls
      } finally {
        this.callsLoading = false
      }
    },
    async openCall(c, autoplay) {
      const resp = await requestFunctions.sendDataAsync(
        'https://api.client.za-bota.com/v1/dialog-cache/call-path',
        { bot_uuid: this.bot_uuid, id: c.id },
        'GET'
      )
      if (!resp || resp.status !== 'success') return
      // ждём тихую перезагрузку карты (openCalls) — иначе свежие связки этого звонка ещё не в блоках
      if (this._refreshPromise) {
        try { await this._refreshPromise } catch (e) { /* карта останется прежней */ }
      }
      this._callRaw = resp.data
      this.recPos = -1
      this.buildCallView()
      if (autoplay) {
        this.$nextTick(() => {
          const el = this.$refs.recAudio
          if (el) {
            el.playbackRate = this.recSpeed
            el.play()
          }
        })
      }
      this.selectedId = null
      const first = this.activeCall.turns.find((t) => t.nodeId)
      if (first) this.centerOn(this.nodeById[first.nodeId])
    },
    buildCallView() {
      // сопоставление ходов звонка с блоками карты; зовётся и ПОСЛЕ каждого rebuild —
      // призраки рассасываются, как только их фразы появляются среди блоков
      const data = this._callRaw
      if (!data) return
      let step = 0
      const startMsk = (data.call.answered_at || data.call.created_at).replace(' ', 'T')
      const startUtc = Date.parse(startMsk) - 3 * 3600 * 1000
      const turns = data.turns.map((t) => {
        const nodeId = this._normToNode[normText(t.response)] || null
        step++ // номер получает КАЖДЫЙ ход — лента и карта нумеруются одинаково
        const ts = Date.parse(t.time.slice(0, 19))
        const offset = isNaN(ts) ? null : Math.max(0, Math.round((ts - startUtc) / 1000))
        return { ...t, nodeId, step, offset }
      })
      const ghosts = []
      let lastNode = null
      let ghostRow = 0
      turns.forEach((t) => {
        if (t.nodeId) {
          lastNode = this.nodeById[t.nodeId] || lastNode
          ghostRow = 0
          return
        }
        const gx = lastNode ? lastNode.x + 400 : 40
        const gy = (lastNode ? lastNode.y : 40) + 170 + ghostRow * 150
        ghostRow++
        const g = { id: 'ghost-' + t.step, step: t.step, x: gx, y: gy, h: 120, text: t.response, client: t.client }
        ghosts.push(g)
        t.ghostId = g.id
      })
      this.callGhosts = ghosts
      this.activeCall = { call: data.call, turns }
    },
    centerOnGhost(gid) {
      const g = this.callGhosts.find((x) => x.id === gid)
      if (g) this.centerOn(g)
    },
    clearCall() {
      this.activeCall = null
      this.callGhosts = []
      this._callRaw = null
      this.recPos = -1
    },
    onRecTime(ev) {
      this.recPos = ev.target.currentTime
    },
    setRecSpeed(v) {
      this.recSpeed = v
      const el = this.$refs.recAudio
      if (el) el.playbackRate = v
    },
    recUrl(c) {
      const token = window.localStorage.getItem('API_KEY_ZABOTA')
      return (
        'https://api.client.za-bota.com/v1/dialog-cache/call-audio?bot_uuid=' +
        encodeURIComponent(this.bot_uuid) + '&id=' + c.id + '&token=' + encodeURIComponent(token)
      )
    },
    playUrl(url, key) {
      if (this.audioEl) {
        this.audioEl.pause()
        this.audioEl = null
      }
      if (this.playingKey === key) {
        this.playingKey = null
        return
      }
      this.playingKey = key
      const el = new Audio(url)
      el.onended = () => { if (this.playingKey === key) this.playingKey = null }
      el.onerror = () => { if (this.playingKey === key) this.playingKey = null }
      el.play()
      this.audioEl = el
    },
    ttsUrl(text) {
      const token = window.localStorage.getItem('API_KEY_ZABOTA')
      return (
        'https://api.client.za-bota.com/v1/dialog-cache/tts?bot_uuid=' +
        encodeURIComponent(this.bot_uuid) +
        '&text=' + encodeURIComponent(text) +
        '&token=' + encodeURIComponent(token)
      )
    },
    bestAudioText(n) {
      // что реально можно прослушать у блока: канон, иначе самая частая ОЗВУЧЕННАЯ формулировка,
      // иначе канон (уйдёт в синтез-превью)
      if (this.voiced[normText(n.canon)]) return n.canon
      const v = n.variants.find((x) => x.voiced)
      return v ? v.text : n.canon
    },
    play(text, key) {
      if (this.audioEl) {
        this.audioEl.pause()
        this.audioEl = null
      }
      if (this.playingKey === key) {
        this.playingKey = null
        return
      }
      this.playingKey = key
      // есть готовая озвучка — играем её из кэша; нет — синтезируем превью голосом бота
      const url = this.voiced[normText(text)] ? this.audioUrl(text) : this.ttsUrl(text)
      const el = new Audio(url)
      el.onended = () => {
        if (this.playingKey === key) this.playingKey = null
      }
      el.onerror = () => {
        if (this.playingKey === key) this.playingKey = null
      }
      el.play()
      this.audioEl = el
    },
    pages(len) {
      return Math.max(1, Math.ceil(len / 6))
    },
    short(text, n = 120) {
      if (!text) return ''
      return text.length > n ? text.slice(0, n) + '…' : text
    },
    pct(x) {
      return Math.round(x * 100)
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" />

    <div v-if="loading" class="text-center my-5">
      <b-spinner variant="primary"></b-spinner>
      <p class="text-muted mt-2">Загружаю кэш и строю смысловую карту…</p>
    </div>

    <b-alert v-else-if="error" show variant="danger">{{ error }}</b-alert>

    <template v-else>
      <!-- Холст (по умолчанию на весь экран; панели плавают поверх) -->
      <div
        ref="viewport"
        class="sm-viewport"
        :class="{ 'sm-panning': panning, 'sm-full': fullscreen }"
        @mousedown="onCanvasDown"
        @mousemove="onMove"
        @mouseup="onUp"
        @mouseleave="onUp"
        @wheel="onWheel"
        @dblclick.self="selectedId = null"
      >
        <!-- Плавающая панель управления -->
        <div class="sm-bar">
          <button
            class="btn btn-sm"
            :class="callsOpen ? 'btn-primary' : 'btn-outline-primary'"
            title="Совершённые диалоги: запись, путь по схеме, правка"
            @click="openCalls"
          >📞 Диалоги</button>
          <div class="btn-group btn-group-sm">
            <button
              class="btn"
              :class="mode === 'semantic' ? 'btn-primary' : 'btn-outline-primary'"
              @click="setMode('semantic')"
            >Смыслы</button>
            <button
              class="btn"
              :class="mode === 'verbatim' ? 'btn-primary' : 'btn-outline-primary'"
              @click="setMode('verbatim')"
            >Дословно</button>
          </div>
          <input
            v-model="search"
            type="search"
            class="form-control form-control-sm sm-search"
            placeholder="Поиск… (Enter — следующий)"
            @keydown.enter="onSearchEnter"
          />
          <span v-if="searchHitIds" class="text-primary small">найдено: {{ Object.keys(searchHitIds).length }}</span>
          <button
            v-if="mode === 'semantic'"
            class="btn btn-sm"
            :class="showSuggest ? 'btn-warning' : 'btn-outline-secondary'"
            :title="'Показывать пунктиром похожие узлы для склейки'"
            @click="showSuggest = !showSuggest"
          >Склейки: {{ openSuggestions.length }}</button>
          <button
            v-if="mode === 'semantic' && openSuggestions.length"
            class="btn btn-sm btn-primary"
            title="Разобрать склейки по одной: после каждого решения откроется следующая"
            @click="startReview"
          >▶ Разобрать по одной</button>
          <button class="btn btn-sm btn-outline-secondary" @click="fit">К началу</button>
          <button class="btn btn-sm btn-outline-danger ml-1" title="Полностью очистить кэш ответов бота (с подтверждением и резервной копией)" @click="clearBotCache">Очистить кэш…</button>
          <button
            v-if="Object.keys(deleted).length + Object.keys(removedLinks).length + Object.keys(splits).length"
            class="btn btn-sm"
            :class="trashOpen ? 'btn-secondary' : 'btn-outline-secondary'"
            title="Удалённые блоки и убранные связи — можно восстановить"
            @click="trashOpen = !trashOpen; selectedId = null; suggestSel = null"
          >🗑 {{ Object.keys(deleted).length + Object.keys(removedLinks).length + Object.keys(splits).length }}</button>
          <span class="text-muted small sm-stats">блоков <b>{{ nodes.length }}</b> · связок <b>{{ stats ? stats.llm_docs : '—' }}</b> · озвучек <b>{{ stats ? stats.audio_docs : '—' }}</b></span>
          <button
            class="btn btn-sm btn-outline-secondary ml-auto"
            :title="fullscreen ? 'Свернуть в страницу' : 'На весь экран'"
            @click="fullscreen = !fullscreen"
          >{{ fullscreen ? '⤡' : '⤢' }}</button>
        </div>

        <div class="sm-world" :style="{ transform: `translate(${tx}px,${ty}px) scale(${scale})` }">
          <svg class="sm-edges" :width="worldSize.w" :height="worldSize.h">
            <defs>
              <marker id="arrB" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 z" fill="#536de6"></path>
              </marker>
              <marker id="arrG" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 z" fill="#2e9e6b"></path>
              </marker>
            </defs>
            <path v-for="l in suggestLinks" :key="'s' + l.s.key" :d="l.d" class="sm-sedge" />
            <path v-for="(sg, i) in pathSegs" :key="'p' + i" :d="sg.d" class="sm-pedge" marker-end="url(#arrG)" />
            <path v-for="(e, i) in visibleEdges" :key="'e' + i" :d="edgePath(e)" class="sm-edge" marker-end="url(#arrB)" />
          </svg>
          <!-- чипы предложений склейки прямо на холсте -->
          <div
            v-for="l in suggestLinks"
            :key="'sc' + l.s.key"
            class="sm-schip"
            :style="{ left: l.mx + 'px', top: l.my + 'px' }"
            :title="'«' + l.s.aText + '»\n«' + l.s.bText + '»'"
            :class="{ 'sm-schip-warn': l.s.ctxDiff }"
            @click="openSuggest(l)"
          >≈{{ pct(l.s.sim) }}% склеить?</div>
          <div
            v-for="(e, i) in visibleEdges"
            :key="'l' + i"
            class="sm-elabel"
            :style="{ left: edgeMid(e).x + 'px', top: edgeMid(e).y + 'px' }"
            :title="e.texts.join('\n')"
          ><b v-if="e.count > 1">×{{ e.count }}</b> {{ short(e.label, 60) }}</div>

          <div
            v-for="gn in callGhosts"
            :key="gn.id"
            class="sm-node sm-ghost sm-onpath"
            :class="{ 'sm-playingnode': playingNodeId === gn.id }"
            :style="{ left: gn.x + 'px', top: gn.y + 'px' }"
            title="Этого хода нет в кэше — фраза сгенерирована LLM и не сохранена (или отсеяна правилом кэшируемости)"
          >
            <span class="sm-step">{{ gn.step }}</span>
            <div class="sm-nhead"><span class="badge badge-soft-warning">нет в кэше</span></div>
            <div v-if="gn.client" class="sm-ghost-client">👤 «{{ gn.client }}»</div>
            <div class="sm-ntext">{{ gn.text }}</div>
          </div>
          <div
            v-for="n in nodes"
            :key="n.id"
            class="sm-node"
            :class="{
              'sm-sel': selectedId === n.id,
              'sm-dim': (neighborIds && !neighborIds[n.id]) || (searchHitIds && !searchHitIds[n.id]) || (pathSet && !pathSet[n.id]),
              'sm-onpath': pathSet && pathSet[n.id],
              'sm-playingnode': playingNodeId === n.id,
              'sm-hit': searchHitIds && searchHitIds[n.id],
            }"
            :style="{ left: n.x + 'px', top: n.y + 'px' }"
            @mousedown="onNodeDown(n, $event)"
            @click="clickNode(n)"
            @mouseenter="hoverId = n.id"
            @mouseleave="hoverId = null"
          >
            <span v-if="pathSet && pathSet[n.id]" class="sm-step">{{ pathSet[n.id] }}</span>
            <!-- ВХОДЫ СВЕРХУ БЛОКА (реш. Сергея 23.07 вместо леса стрелок) -->
            <div v-if="n.entries.length" class="sm-entries">
              <span
                v-for="(en, k) in n.entries.slice(0, 3)"
                :key="k"
                class="sm-entry"
                :title="'клиент: «' + en.label + '»'"
                @click.stop="en.fromId && goTo(en.fromId)"
              >«{{ short(en.label, 26) }}»<b v-if="en.count > 1"> ×{{ en.count }}</b></span>
              <span v-if="n.entries.length > 3" class="sm-entry sm-entry-more">+{{ n.entries.length - 3 }}</span>
            </div>
            <div class="sm-nhead">
              <button
                class="btn btn-sm sm-play"
                :class="playingKey === n.id ? 'btn-primary' : 'btn-outline-primary'"
                :title="n.voiced ? 'Прослушать озвучку из кэша' : (n.voicedAny ? 'Канон не озвучен — играет озвучка формулировки из кэша' : 'Озвучки нет — прослушать синтез голосом бота')"
                @click.stop="play(bestAudioText(n), n.id)"
              ><i class="uil" :class="playingKey === n.id ? 'uil-pause' : 'uil-play'"></i></button>
              <span v-if="n.voiced" class="badge badge-soft-success">озвучено</span>
              <span v-else-if="n.voicedAny" class="badge badge-soft-info" title="Озвучены не все формулировки; ▶ играет ту, что озвучена">озвучено {{ n.voicedCount }} из {{ n.variants.length }}</span>
              <span v-else class="badge badge-soft-warning">нет озвучки</span>
              <span v-if="n.variants.length > 1" class="badge badge-soft-primary" title="Сколькими формулировками бот говорил эту фразу — варианты внутри, канон выбирается в карточке">{{ n.variants.length }} вар.</span>
              <span v-if="n.frag" class="badge badge-soft-secondary" title="Обрывок цепочки: предыдущая фраза бота не нашлась в кэше дословно. Это НЕ начало диалога — склеится при смысловом связывании">обрывок</span>
              <span v-if="n.vars" class="badge badge-soft-danger" title="Числа/даты в тексте — при выдаче из кэша нужна страховка переменных">⚠</span>
              <span class="sm-uses ml-auto">×{{ n.uses }}</span>
            </div>
            <div v-if="editingId !== n.id" class="sm-ntext" title="Двойной клик — исправить текст" @dblclick.stop="startEditNode(n)">{{ n.canon }}</div>
            <div v-else class="sm-nedit" @mousedown.stop @click.stop>
              <textarea v-model="editingText" class="form-control form-control-sm" rows="3"></textarea>
              <div class="mt-1">
                <button class="btn btn-sm btn-primary mr-1" :disabled="!editingText.trim()" @click="saveEditNode(n)">Сохранить</button>
                <button
                  class="btn btn-sm mr-1"
                  :class="playingKey === 'inline' ? 'btn-primary' : 'btn-outline-primary'"
                  :disabled="!editingText.trim()"
                  @click="play(editingText.trim(), 'inline')"
                ><i class="uil" :class="playingKey === 'inline' ? 'uil-pause' : 'uil-play'"></i></button>
                <button class="btn btn-sm btn-outline-secondary" @click="editingId = null">Отмена</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Лента диалогов (слева) -->
      <div v-if="callsOpen" class="sm-panel-left card">
        <div class="card-body">
          <div class="d-flex align-items-center mb-2">
            <h5 class="my-0">Диалоги</h5>
            <button v-if="activeCall" class="btn btn-sm btn-outline-secondary ml-2" @click="clearCall">← к списку</button>
            <button class="close ml-auto" type="button" @click.stop="callsOpen = false; activeCall = null">×</button>
          </div>

          <template v-if="!activeCall">
            <p v-if="callsLoading" class="text-muted">Загружаю звонки…</p>
            <p v-else-if="!callsList.length" class="text-muted">Звонков пока нет.</p>
            <div v-for="c in callsList" :key="c.id" class="sm-call" @click="openCall(c)">
              <div class="d-flex align-items-center">
                <button
                  v-if="c.has_record"
                  class="btn btn-sm sm-play mr-2 btn-outline-primary"
                  title="Открыть диалог и включить запись"
                  @click.stop="openCall(c, true)"
                ><i class="uil uil-play"></i></button>
                <span class="flex-grow-1">
                  <b>{{ c.phone || 'без номера' }}</b>
                  <span class="d-block text-muted small">{{ c.created_at }} · {{ c.duration }} сек · {{ c.status }}</span>
                </span>
              </div>
            </div>
          </template>

          <template v-else>
            <p class="mb-1"><b>{{ activeCall.call.phone }}</b> <span class="text-muted small">{{ activeCall.call.created_at }} · {{ activeCall.call.duration }} сек</span></p>
            <audio
              ref="recAudio"
              class="sm-recplayer mb-1"
              controls
              preload="metadata"
              :src="recUrl(activeCall.call)"
              @timeupdate="onRecTime"
              @play="setRecSpeed(recSpeed)"
            ></audio>
            <div class="mb-2">
              <span class="text-muted small mr-1">Скорость:</span>
              <button
                v-for="v in [1, 1.25, 1.5, 2]"
                :key="v"
                class="btn btn-sm py-0 px-2 mr-1"
                :class="recSpeed === v ? 'btn-primary' : 'btn-outline-secondary'"
                @click="setRecSpeed(v)"
              >{{ v }}×</button>
            </div>
            <p v-if="!activeCall.turns.length" class="text-muted">Ходы этого звонка в кэше не найдены.</p>
            <div
              v-for="(t, i) in activeCall.turns"
              :key="i"
              class="sm-turn"
              :class="{ 'sm-turn-off': !t.nodeId && !t.ghostId, 'sm-turn-playing': i === playingTurnIdx }"
              @click="t.nodeId ? goTo(t.nodeId) : (t.ghostId && centerOnGhost(t.ghostId))"
            >
              <div class="sm-turn-client">
                <span v-if="t.step" class="sm-step-inline">{{ t.step }}</span>
                <template v-if="t.client">👤 «{{ t.client }}»</template>
                <template v-else><span class="text-muted">реплика клиента не сохранена</span></template>
                <span class="text-muted small"> {{ t.time.slice(11, 19) }}<template v-if="t.offset !== null"> · {{ t.offset }}с</template></span>
              </div>
              <div class="sm-turn-bot">🤖 {{ t.response }}
                <span class="badge" :class="t.from_cache ? 'badge-soft-success' : 'badge-soft-warning'">{{ t.from_cache ? 'из кэша' : 'новая (LLM)' }}</span>
              </div>
            </div>
            <p class="text-muted small mb-0 mt-2">Клик по ходу — блок откроется на карте, там можно править текст и связи.</p>
          </template>
        </div>
      </div>

      <!-- Корзина удалённых блоков -->
      <div v-if="trashOpen" class="sm-panel card">
        <div class="card-body">
          <div class="d-flex align-items-center mb-2">
            <h5 class="my-0">Корзина</h5>
            <button class="close ml-auto" type="button" @click.stop="trashOpen = false">×</button>
          </div>
          <h6 v-if="Object.keys(deleted).length" class="mb-1">Удалённые блоки ({{ Object.keys(deleted).length }})</h6>
          <div v-for="(text, key) in deleted" :key="key" class="sm-reply">
            «{{ short(text, 120) }}»
            <div class="mt-1">
              <button class="btn btn-sm btn-outline-primary" @click.stop="restoreNode(key)">Восстановить</button>
            </div>
          </div>
          <h6 v-if="Object.keys(splits).length" class="mb-1 mt-3">Вытащенные в отдельный блок ({{ Object.keys(splits).length }})</h6>
          <div v-for="(text, key) in splits" :key="'sp' + key" class="sm-reply">
            «{{ short(text, 120) }}»
            <div class="mt-1">
              <button class="btn btn-sm btn-outline-primary" @click.stop="unsplit(key)">Разрешить склейку снова</button>
            </div>
          </div>
          <h6 v-if="Object.keys(removedLinks).length" class="mb-1 mt-3">Убранные связи ({{ Object.keys(removedLinks).length }})</h6>
          <div v-for="(label, key) in removedLinks" :key="'rl' + key" class="sm-reply">
            {{ label }}
            <div class="mt-1">
              <button class="btn btn-sm btn-outline-primary" @click.stop="restoreLink(key)">Вернуть связь</button>
            </div>
          </div>
          <p class="text-muted small mt-2 mb-0">Из боевого кэша пока ничего не удалено — блоки и связи скрыты с карты; физическое удаление будет на этапе записи по вашему ✅.</p>
        </div>
      </div>

      <!-- Карточка сравнения пары для склейки -->
      <div v-else-if="suggestSel" class="sm-panel card">
        <div class="card-body">
          <div class="d-flex align-items-center mb-2">
            <h5 class="my-0">Склеить эти узлы?</h5>
            <span class="badge badge-soft-secondary ml-2">похожесть {{ pct(suggestSel.sim) }}%</span>
            <span v-if="review" class="badge badge-soft-info ml-2">осталось {{ reviewQueue().length }}</span>
            <button class="close ml-auto" type="button" @click.stop="suggestSel = null; review = false">×</button>
          </div>
          <p class="text-muted small mb-1">Отметьте, каким текстом бот будет говорить склеенную фразу:</p>
          <label
            v-for="opt in [{ v: 'a', id: suggestSel.aId, text: suggestSel.aText }, { v: 'b', id: suggestSel.bId, text: suggestSel.bText }]"
            :key="opt.v"
            class="sm-compare d-flex"
            :class="{ 'sm-canon': mergeChoice === opt.v && !mergeCustom.trim() }"
          >
            <input v-model="mergeChoice" type="radio" :value="opt.v" class="mr-2 mt-1" />
            <span class="flex-grow-1">
              <template v-if="topContext(opt.id)">
                <span class="sm-dlg-prev"><span class="sm-dlg-role">бот до этого</span>{{ short(topContext(opt.id).prev, 90) }}</span>
                <span class="sm-dlg-client"><span class="sm-dlg-role">клиент</span>«{{ short(topContext(opt.id).label, 60) }}»<b v-if="topContext(opt.id).count > 1"> ×{{ topContext(opt.id).count }}</b><span v-if="topContext(opt.id).more" class="text-muted"> · +{{ topContext(opt.id).more }} других входов</span></span>
              </template>
              <span class="sm-dlg-answer"><span class="sm-dlg-role sm-dlg-role-a">ответ бота</span>«{{ opt.text }}»</span>
            </span>
            <button class="btn btn-sm btn-link py-0 px-1" title="Показать на карте" @click.prevent.stop="goTo(opt.id)">👁</button>
          </label>
          <p v-if="suggestSel.ctxDiff" class="small text-danger mt-2 mb-1">⚠ Эти фразы встречаются в РАЗНЫХ местах диалога (нет общего предыдущего хода) — скорее всего, это разные по роли фразы (вопрос ≠ переспрос). Склеивать не рекомендуется.</p>
          <p v-if="suggestSel.vars" class="small text-warning mt-2 mb-2">⚠ В одной из фраз есть числа/даты — клеить осторожно.</p>
          <textarea
            v-model="mergeCustom"
            class="form-control form-control-sm mt-2"
            rows="2"
            placeholder="Или впишите свой текст итоговой фразы (не обязательно)"
          ></textarea>
          <div class="mt-1">
            <button
              v-if="mergeCustom.trim()"
              class="btn btn-sm"
              :class="playingKey === 'mergecustom' ? 'btn-primary' : 'btn-outline-primary'"
              title="Услышать, как будет звучать ваш текст голосом бота"
              @click="play(mergeCustom.trim(), 'mergecustom')"
            ><i class="uil" :class="playingKey === 'mergecustom' ? 'uil-pause' : 'uil-play'"></i> Синтезировать</button>
          </div>
          <div class="mt-3">
            <button class="btn btn-success mr-2" @click="decideSuggestion(suggestSel, 'merge')">Склеить</button>
            <button class="btn btn-outline-secondary mr-2" @click="decideSuggestion(suggestSel, 'skip')">Оставить раздельно</button>
            <button v-if="review" class="btn btn-link" title="Отложить — решу позже" @click="skipSuggestion">Пропустить</button>
          </div>
          <p class="text-muted small mt-3 mb-0">Решение сохраняется в этом браузере; запись в кэш — на следующем этапе.</p>
        </div>
      </div>

      <!-- Карточка выбранного узла -->
      <div v-else-if="selectedNode" class="sm-panel card">
        <div class="card-body">
          <div class="d-flex align-items-center mb-2">
            <h5 class="my-0">Фраза бота</h5>
            <button class="close ml-auto" type="button" @click.stop="selectedId = null">×</button>
          </div>
          <p class="text-muted small mb-1">Текст, которым бот говорит эту фразу — можно исправить прямо здесь:</p>
          <textarea v-model="editText" class="form-control sm-edittext" rows="3"></textarea>
          <div class="mt-1 mb-2">
            <button
              class="btn btn-sm mr-1"
              :class="playingKey === 'edit' ? 'btn-primary' : 'btn-outline-primary'"
              :disabled="!editText.trim()"
              title="Услышать этот текст голосом бота (готовая озвучка или синтез)"
              @click="play(editText.trim(), 'edit')"
            ><i class="uil" :class="playingKey === 'edit' ? 'uil-pause' : 'uil-play'"></i> Синтезировать</button>
            <button
              class="btn btn-sm btn-primary"
              :disabled="saving || !editText.trim()"
              title="Озвучить новый текст и заменить им ответы бота в этом блоке — бот начнёт говорить именно так со следующего звонка"
              @click="saveBlock(selectedNode, editText.trim())"
            >{{ saving ? 'Сохраняю…' : 'Перегенерировать и сохранить' }}</button>
            <button
              v-if="selectedNode.voiced"
              class="btn btn-sm btn-outline-secondary ml-1"
              :disabled="saving || !editText.trim()"
              title="Записать озвучку заново тем же текстом — если в текущей записи слышен брак"
              @click="revoiceBlock(selectedNode, editText.trim())"
            ><i class="uil uil-repeat"></i> Переозвучить</button>
            <span v-if="saveMsg" class="text-muted small ml-1 d-block mt-1">{{ saveMsg }}</span>
          </div>
          <div class="mb-2">
            <span class="badge badge-soft-secondary mr-1">в кэше ×{{ selectedNode.uses }}</span>
            <span v-if="selectedNode.vars" class="badge badge-soft-danger mr-1">⚠ переменные</span>
            <span v-if="selectedNode.customCanon" class="badge badge-soft-primary" title="Текст фразы исправлен вручную — на этапе записи получит новую озвучку">текст исправлен</span>
          </div>


          <template v-if="selectedNode.variants.length > 1">
            <button class="btn btn-sm btn-link px-0 mt-2" @click="showVariants = !showVariants">
              {{ showVariants ? 'Скрыть' : 'Показать' }} формулировки из кэша ({{ selectedNode.variants.length }})
            </button>
            <template v-if="showVariants">
            <input v-model="varQ" type="search" class="form-control form-control-sm mb-1" placeholder="Поиск по формулировкам…" />
            <p class="text-muted small mb-1">Клик подставляет формулировку в поле выше:</p>
            <div
              v-for="(v, i) in pageVariants"
              :key="varPage + '-' + i"
              class="sm-variant"
              :class="{ 'sm-canon': v.text === editText.trim() }"
              @click="editText = v.text"
            >
              <input type="radio" :checked="v.text === editText.trim()" class="mr-2" />
              <span class="sm-variant-text">{{ v.text }}</span>
              <span class="text-muted small ml-1">×{{ v.uses }}</span>
              <span v-if="v.voiced" class="badge badge-soft-success ml-1">озв.</span>
              <button
                class="btn btn-sm btn-link py-0 px-1"
                :title="v.voiced ? 'Прослушать озвучку этой формулировки из кэша' : 'Озвучки нет — прослушать синтез'"
                @click.stop="play(v.text, 'var-' + v.text)"
              ><i class="uil" :class="playingKey === 'var-' + v.text ? 'uil-pause' : 'uil-play'"></i></button>
              <button
                v-if="selectedNode.variants.length > 1"
                class="btn btn-sm btn-link text-danger py-0 px-1"
                title="Это ДРУГАЯ фраза, склеена ошибочно — вытащить в отдельный блок (вернуть — через корзину 🗑)"
                @click.stop="splitVariant(selectedNode, v)"
              >✂</button>
            </div>
            <div v-if="filtVariants.length > 6" class="sm-pager">
              <button class="btn btn-sm btn-outline-secondary" :disabled="varPage === 0" @click="varPage--">‹</button>
              <span class="text-muted small">{{ varPage + 1 }} / {{ pages(filtVariants.length) }} · всего {{ filtVariants.length }}</span>
              <button class="btn btn-sm btn-outline-secondary" :disabled="(varPage + 1) * 6 >= filtVariants.length" @click="varPage++">›</button>
            </div>
            <p v-if="!filtVariants.length" class="text-muted small mb-0">ничего не найдено</p>
            </template>
          </template>

          <h6 class="mb-1 mt-3">Клиент отвечает ({{ selOutgoing.length }})</h6>
          <input v-if="selOutgoing.length > 6" v-model="outQ" type="search" class="form-control form-control-sm mb-1" placeholder="Поиск по репликам и ответам…" />
          <div v-for="(e, i) in pageOut" :key="'o' + outPage + '-' + i" class="sm-reply" @click="goTo(e.dstId)">
            <b v-if="e.count > 1">×{{ e.count }}</b> «{{ short(e.label, 70) }}»
            <div class="text-muted small">→ {{ short(nodeById[e.dstId] ? nodeById[e.dstId].canon : '', 80) }}</div>
          </div>
          <div v-if="filtOut.length > 6" class="sm-pager">
            <button class="btn btn-sm btn-outline-secondary" :disabled="outPage === 0" @click="outPage--">‹</button>
            <span class="text-muted small">{{ outPage + 1 }} / {{ pages(filtOut.length) }} · всего {{ filtOut.length }}</span>
            <button class="btn btn-sm btn-outline-secondary" :disabled="(outPage + 1) * 6 >= filtOut.length" @click="outPage++">›</button>
          </div>

          <template v-if="callIncoming.length">
            <h6 class="mb-1 mt-3">В открытом звонке сюда пришли ({{ callIncoming.length }})</h6>
            <div v-for="(t, i) in callIncoming" :key="'ci' + i" class="sm-reply">
              👤 «{{ short(t.client, 70) }}» <span class="text-muted small">{{ t.time.slice(11, 19) }}</span>
              <span class="d-block text-muted small">после: {{ short(t.prev, 80) }}</span>
              <span v-if="!t.inCache" class="badge badge-soft-warning" title="Платформа не записала эту связку в кэш связок — на карте такого входа нет; при повторе этой реплики бот снова пойдёт в LLM">связки нет в кэше</span>
            </div>
          </template>

          <h6 class="mb-1 mt-3">Как сюда попадают ({{ selIncoming.length }})</h6>
          <input v-if="selIncoming.length > 6" v-model="inQ" type="search" class="form-control form-control-sm mb-1" placeholder="Поиск по репликам и предыдущим фразам…" />
          <div v-for="(e, i) in pageIn" :key="'i' + inPage + '-' + i" class="sm-reply d-flex align-items-start" @click="goTo(e.srcId)">
            <span class="flex-grow-1">
              <b v-if="e.count > 1">×{{ e.count }}</b> «{{ short(e.label, 70) }}»
              <span class="d-block text-muted small">после: {{ short(nodeById[e.srcId] ? nodeById[e.srcId].canon : '', 80) }}</span>
            </span>
            <button
              class="btn btn-sm btn-link text-danger py-0 px-1"
              title="Эта реплика не должна вести в этот блок — убрать связь (вернуть можно через корзину 🗑)"
              @click.stop="removeLink(e)"
            >✕</button>
          </div>
          <div v-if="filtIn.length > 6" class="sm-pager">
            <button class="btn btn-sm btn-outline-secondary" :disabled="inPage === 0" @click="inPage--">‹</button>
            <span class="text-muted small">{{ inPage + 1 }} / {{ pages(filtIn.length) }} · всего {{ filtIn.length }}</span>
            <button class="btn btn-sm btn-outline-secondary" :disabled="(inPage + 1) * 6 >= filtIn.length" @click="inPage++">›</button>
          </div>

          <div class="mt-3">
            <button class="btn btn-sm btn-outline-danger" @click="deleteNode(selectedNode)">🗑 Удалить блок</button>
            <span class="text-muted small ml-2">блок скроется с карты; вернуть — через 🗑 в панели сверху</span>
          </div>
          <p class="text-muted small mt-3 mb-0">
            Каноны, склейки и удаления пока сохраняются локально в браузере. Запись в боевой кэш — следующий этап, после проверки безопасности.
          </p>
        </div>
      </div>
    </template>
  </Layout>
</template>

<style lang="scss" scoped>
.sm-search { width: 240px; }
.sm-viewport {
  position: relative;
  height: calc(100vh - 200px);
  min-height: 520px;
  overflow: hidden;
  cursor: grab;
  border: 1px solid #e3e6ef;
  border-radius: 8px;
  background: #f4f6fb;
  background-image: radial-gradient(#d9dfec 1.2px, transparent 1.2px);
  background-size: 26px 26px;
}
// на весь экран ЦЕЛИКОМ, поверх шапки и сайдбара (25.07: «налипший» текст страницы не должен просвечивать)
.sm-full {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  height: auto;
  min-height: 0;
  z-index: 1050;
  border-radius: 0;
  border: none;
}
.sm-bar {
  position: absolute;
  top: 10px;
  left: 10px;
  right: 10px;
  z-index: 30;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #e3e6ef;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(29, 36, 51, 0.08);
  cursor: default;
}
.sm-stats b { font-variant-numeric: tabular-nums; }
.sm-sedge {
  fill: none;
  stroke: #e6a23c;
  stroke-width: 1.6;
  stroke-dasharray: 6 5;
  opacity: 0.55;
}
.sm-schip {
  position: absolute;
  transform: translate(-50%, -50%);
  padding: 2px 10px;
  border-radius: 999px;
  background: #fff7e8;
  border: 1px solid #e6a23c;
  color: #9c6d16;
  font-size: 11px;
  white-space: nowrap;
  cursor: pointer;
  z-index: 6;
  &:hover { background: #ffedcc; }
}
.sm-schip-warn { border-color: #e05b5b; color: #a33; }
.sm-compare {
  border: 1px solid #e3e6ef;
  border-radius: 8px;
  padding: 8px 11px;
  font-size: 13px;
  cursor: pointer;
  margin-bottom: 8px;
  &:hover { border-color: #536de6; }
}
.sm-dlg-prev,
.sm-dlg-client,
.sm-dlg-answer { display: block; }
.sm-dlg-prev { color: #98a1b8; font-size: 12px; }
.sm-dlg-client { color: #5b6478; font-size: 12px; margin: 3px 0 6px 12px; b { color: #536de6; } }
.sm-dlg-answer { font-size: 13px; }
.sm-dlg-role {
  display: inline-block;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #98a1b8;
  border: 1px solid #e3e6ef;
  border-radius: 999px;
  padding: 0 6px;
  margin-right: 6px;
  vertical-align: 1px;
}
.sm-dlg-role-a { color: #536de6; border-color: #b9c2dd; }
.sm-panning { cursor: grabbing; }
.sm-pedge { fill: none; stroke: #2e9e6b; stroke-width: 3; opacity: 0.85; }
.sm-playingnode { outline: 3px solid #e6a23c !important; outline-offset: 2px; box-shadow: 0 0 14px rgba(230, 162, 60, 0.55); }
.sm-turn-playing { border-color: #e6a23c; background: #fff7e8; }
.sm-ghost { border: 2px dashed #e6a23c; background: #fffdf5; }
.sm-ghost .sm-ghost-client { font-size: 0.78rem; color: #888; margin: 2px 0; }
.sm-recplayer { width: 100%; height: 36px; }
.sm-onpath { outline: 2px solid #2e9e6b; outline-offset: 1px; }
.sm-step {
  position: absolute;
  top: -11px;
  left: -11px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #2e9e6b;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 7;
}
.sm-panel-left {
  position: fixed;
  top: 66px;
  left: 12px;
  width: 400px;
  max-width: 92vw;
  max-height: calc(100vh - 90px);
  overflow-y: auto;
  z-index: 1060;
  box-shadow: 0 6px 24px rgba(29, 36, 51, 0.18);
  .close { font-size: 28px; line-height: 1; padding: 0 6px; margin-right: 8px; }
}
.sm-call {
  border: 1px solid #e3e6ef;
  border-radius: 8px;
  padding: 7px 10px;
  margin-bottom: 6px;
  cursor: pointer;
  &:hover { border-color: #536de6; }
}
.sm-turn {
  border: 1px solid #e3e6ef;
  border-radius: 8px;
  padding: 6px 10px;
  margin-bottom: 6px;
  font-size: 12.5px;
  cursor: pointer;
  &:hover { border-color: #2e9e6b; }
}
.sm-turn-off { opacity: 0.6; cursor: default; }
.sm-turn-client { color: #5b6478; }
.sm-turn-bot { margin-top: 2px; }
.sm-step-inline {
  display: inline-flex;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #2e9e6b;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
}
.sm-world { position: absolute; left: 0; top: 0; transform-origin: 0 0; }
.sm-edges { position: absolute; left: 0; top: 0; overflow: visible; pointer-events: none; }
.sm-edge { fill: none; stroke: #536de6; stroke-width: 2.2; opacity: 0.85; }
.sm-elabel {
  position: absolute;
  transform: translate(-50%, -50%);
  max-width: 220px;
  padding: 2px 9px;
  border-radius: 999px;
  background: #eef1fd;
  border: 1px solid #536de6;
  color: #33406e;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
  z-index: 5;
}
.sm-node {
  position: absolute;
  width: 340px;
  background: #fff;
  border: 1px solid #dfe3ee;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(29, 36, 51, 0.08), 0 5px 14px rgba(29, 36, 51, 0.06);
  cursor: pointer;
  user-select: none;
  transition: opacity 0.15s;
  &:hover { border-color: #b9c2dd; }
}
.sm-sel { border-color: #536de6; box-shadow: 0 0 0 2px rgba(83, 109, 230, 0.25); }
.sm-dim { opacity: 0.15; }
.sm-hit { border-color: #e6a23c; box-shadow: 0 0 0 2px rgba(230, 162, 60, 0.3); }
.sm-entries {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  padding: 6px 10px 0;
}
.sm-entry {
  font-size: 10.5px;
  line-height: 1.5;
  padding: 0 7px;
  border-radius: 999px;
  background: #f0f2f8;
  border: 1px solid #dfe3ee;
  color: #5b6478;
  white-space: nowrap;
  b { color: #536de6; }
  &:hover { border-color: #536de6; color: #33406e; }
}
.sm-entry-more { background: transparent; border-style: dashed; }
.sm-nhead {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 10px 0;
  .badge { font-size: 10px; }
}
.sm-play { padding: 0 7px; line-height: 1.6; border-radius: 50%; }
.sm-uses { color: #98a1b8; font-size: 11px; }
.sm-ntext {
  padding: 5px 12px 10px;
  font-size: 12.5px;
  line-height: 1.45;
}
.sm-panel {
  position: fixed;
  top: 66px;
  right: 24px;
  width: 400px;
  max-width: 92vw;
  max-height: calc(100vh - 90px);
  overflow-y: auto;
  .close { font-size: 28px; line-height: 1; padding: 0 6px; margin-right: 8px; }
  z-index: 1060; // выше фуллскрин-холста (1001), иначе карточка «не открывается»
  box-shadow: 0 6px 24px rgba(29, 36, 51, 0.18);
}
.sm-fulltext { font-size: 0.95rem; white-space: pre-wrap; }
.sm-edittext { font-size: 0.9rem; line-height: 1.4; }
.sm-nedit { padding: 4px 10px 10px; }
.sm-variant {
  display: flex;
  align-items: flex-start;
  padding: 6px 9px;
  border: 1px solid #e3e6ef;
  border-radius: 7px;
  margin-bottom: 5px;
  font-size: 12.5px;
  cursor: pointer;
  .sm-variant-text { flex: 1; }
  &:hover { border-color: #b9c2dd; }
}
.sm-canon { border-color: #536de6; background: #eef1fd; }
.sm-pager {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  margin: 4px 0 8px;
}
.sm-reply {
  border: 1px solid #e3e6ef;
  border-radius: 7px;
  padding: 6px 9px;
  margin-bottom: 5px;
  font-size: 12.5px;
  cursor: pointer;
  b { color: #536de6; }
  &:hover { border-color: #536de6; }
}
</style>
